import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, HostListener, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { catchError, first, firstValueFrom, of } from 'rxjs';

import { ContactService } from '@core/api/api';
import { Result } from '@core/api/model';
import { ApplicationLeaveService } from '@core/application-leave/application-leave.service';
import { LeaveAware } from '@core/guard/deactivate-guard/leave-aware';
import { LoadingObservable } from '@shared/LoadingObservable';
import { AlertService } from '@shared/alert.service';

@Component({
  selector: 'app-contact-dialog',
  templateUrl: './contact-dialog.component.html',
  styleUrls: ['./contact-dialog.component.scss'],
  standalone: false,
})
export class ContactDialogComponent implements LeaveAware {
  private readonly contactService = inject(ContactService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject<MatDialogRef<ContactDialogComponent>>(MatDialogRef);
  private readonly data = inject<{ userEmail: string }>(MAT_DIALOG_DATA);
  private readonly alertService = inject(AlertService);
  private readonly translate = inject(TranslateService);
  private readonly leaveService = inject(ApplicationLeaveService);

  protected readonly isLoading$ = new LoadingObservable();

  protected contactFormGroup = new FormGroup({
    name: new FormControl<string>('', [Validators.required, Validators.maxLength(50)]),
    email: new FormControl<string>(this.data.userEmail, [Validators.required, Validators.email]),
    message: new FormControl<string>('', Validators.required),
  });

  @HostListener('window:beforeunload', ['$event'])
  private beforeUnload(event: BeforeUnloadEvent) {
    this.leaveService.handleUnload(event, this);
  }

  public sendMessage() {
    const { email, name, message } = this.contactFormGroup.value;
    if (this.contactFormGroup.invalid || !email || !name || !message) {
      return;
    }
    this.contactService
      .sendContactRequest(email, name, message)
      .pipe(
        this.isLoading$.withOp(),
        first(),
        catchError((err: HttpErrorResponse) => {
          return of({ success: false } as Result);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        if (value.success) {
          this.dialogRef.close();
        } else {
          this.alertService.alert(this.translate.instant('contact.SEND_FAILED'));
        }
      });
  }

  @HostListener('document:keydown.escape')
  public async closeDialog() {
    if (!this.isOkToLeave()) {
      const result = await firstValueFrom(
        this.alertService.confirmation(this.translate.instant('contact.DISCARD_MESSAGE')),
      );
      if (!result) {
        return;
      }
    }
    this.dialogRef.close();
  }

  public isOkToLeave(): boolean {
    return !this.contactFormGroup.value.message;
  }
}
